// Кастомный хук горячих клавиш. Без сторонних libs.
//
// Формат combo: `mod+k`, `ctrl+shift+p`, `esc`, `/`.
// `mod` — Command на macOS, Control на остальных.
import { useEffect } from "react";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

interface ParsedCombo {
	key: string;
	ctrl: boolean;
	meta: boolean;
	alt: boolean;
	shift: boolean;
}

function parseCombo(combo: string): ParsedCombo {
	const parts = combo
		.toLowerCase()
		.split("+")
		.map((p) => p.trim());
	const out: ParsedCombo = {
		key: "",
		ctrl: false,
		meta: false,
		alt: false,
		shift: false,
	};
	for (const p of parts) {
		if (p === "mod") {
			if (isMac) out.meta = true;
			else out.ctrl = true;
		} else if (p === "ctrl" || p === "control") {
			out.ctrl = true;
		} else if (p === "meta" || p === "cmd" || p === "command") {
			out.meta = true;
		} else if (p === "alt" || p === "option") {
			out.alt = true;
		} else if (p === "shift") {
			out.shift = true;
		} else {
			out.key = p;
		}
	}
	return out;
}

function matches(event: KeyboardEvent, combo: ParsedCombo): boolean {
	const key = event.key.toLowerCase();
	if (key !== combo.key) return false;
	if (combo.ctrl !== event.ctrlKey) return false;
	if (combo.meta !== event.metaKey) return false;
	if (combo.alt !== event.altKey) return false;
	if (combo.shift !== event.shiftKey) return false;
	return true;
}

/**
 * Цель события — поле ввода (input / textarea / select / contenteditable).
 *
 * Экспортируется, потому что глобальные keydown-слушатели плееров (колода
 * карточек, прохождение теста) обязаны фильтровать ввод ТЕМ ЖЕ правилом:
 * в воркспейсе курса рядом с ними стоит композер репетитора на TipTap
 * (contenteditable), и `1-4` / `M` / `Space` уходили бы в оценку карточки
 * вместо текста. `isContentEditable` покрывает и вложенные узлы ProseMirror —
 * в отличие от `getAttribute("contenteditable")` на самой цели.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	const tag = target.tagName;
	if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
	if (target.isContentEditable) return true;
	return false;
}

export interface UseHotkeyOptions {
	/** Срабатывать ли в input/textarea (по умолчанию — нет). */
	enableInInputs?: boolean;
	/** preventDefault при срабатывании (по умолчанию — true). */
	preventDefault?: boolean;
	/** Отключить хоткей. */
	disabled?: boolean;
}

export function useHotkey(
	combo: string,
	handler: (event: KeyboardEvent) => void,
	options: UseHotkeyOptions = {},
): void {
	const { enableInInputs = false, preventDefault = true, disabled = false } = options;
	useEffect(() => {
		if (disabled) return;
		const parsed = parseCombo(combo);
		const onKey = (event: KeyboardEvent): void => {
			if (!enableInInputs && isEditableTarget(event.target)) return;
			if (!matches(event, parsed)) return;
			if (preventDefault) event.preventDefault();
			handler(event);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [combo, handler, enableInInputs, preventDefault, disabled]);
}
